import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Assignment interfaces
export interface SupplierInfo {
  business_name: string;
  email: string;
  phone: string;
}

export interface Assignment {
  id: string;
  supplier_id: string;
  supplier_info: SupplierInfo;
  tenant_business_name: string;
  business_name: string;
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED";
  is_preferred: boolean;
  assigned_by: string;
  assigned_date: string;
  assignment_notes: string | null;
  custom_payment_terms: string | null;
  custom_delivery_terms: string | null;
  total_orders_placed: number;
  total_amount_spent: string;
  last_order_date: string | null;
  tenant_rating: string;
  tenant_review: string | null;
  created_at: string;
  updated_at: string;
}

export interface RestaurantAssignment {
  tenant_id: string;
  tenant_name: string;
  total_assignments: number;
  assignments: Assignment[];
}

export interface SupplierAssignment {
  supplier_id: string;
  supplier_name: string;
  total_assignments: number;
  assignments: Assignment[];
}

export interface CreateAssignmentRequest {
  tenant_id: string;
  supplier_id: string;
  assignment_notes?: string;
  is_preferred?: boolean;
  custom_payment_terms?: string;
  custom_delivery_terms?: string;
}

// Pagination interface
export interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
  total_pages?: number;
  current_page?: number;
}

// Assignment state interface
interface AssignmentState {
  // Restaurant assignments
  restaurantAssignments: RestaurantAssignment | null;
  restaurantAssignmentsLoading: boolean;
  restaurantAssignmentsPagination: PaginationInfo;

  // Supplier assignments
  supplierAssignments: SupplierAssignment | null;
  supplierAssignmentsLoading: boolean;
  supplierAssignmentsPagination: PaginationInfo;

  // Assignment creation
  assignmentCreateLoading: boolean;
  assignmentCreateFieldErrors: Record<string, string[]> | null;

  // Selected items
  selectedAssignment: Assignment | null;
  selectedRestaurant: string | null;
  selectedSupplier: string | null;

  // Dialog states
  assignmentDialogOpen: boolean;
  viewAssignmentDialogOpen: boolean;
}

// Initial state
const initialState: AssignmentState = {
  // Restaurant assignments
  restaurantAssignments: null,
  restaurantAssignmentsLoading: false,
  restaurantAssignmentsPagination: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
  },

  // Supplier assignments
  supplierAssignments: null,
  supplierAssignmentsLoading: false,
  supplierAssignmentsPagination: {
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: 1,
  },

  // Assignment creation
  assignmentCreateLoading: false,
  assignmentCreateFieldErrors: null,

  // Selected items
  selectedAssignment: null,
  selectedRestaurant: null,
  selectedSupplier: null,

  // Dialog states
  assignmentDialogOpen: false,
  viewAssignmentDialogOpen: false,
};

// Assignment slice
const assignmentSlice = createSlice({
  name: "assignments",
  initialState,
  reducers: {
    // Restaurant assignments
    setRestaurantAssignments: (
      state,
      action: PayloadAction<RestaurantAssignment>,
    ) => {
      state.restaurantAssignments = action.payload;
    },
    setRestaurantAssignmentsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.restaurantAssignmentsLoading = action.payload;
    },
    setRestaurantAssignmentsPagination: (
      state,
      action: PayloadAction<PaginationInfo>,
    ) => {
      state.restaurantAssignmentsPagination = action.payload;
    },

    // Supplier assignments
    setSupplierAssignments: (
      state,
      action: PayloadAction<SupplierAssignment>,
    ) => {
      state.supplierAssignments = action.payload;
    },
    setSupplierAssignmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.supplierAssignmentsLoading = action.payload;
    },
    setSupplierAssignmentsPagination: (
      state,
      action: PayloadAction<PaginationInfo>,
    ) => {
      state.supplierAssignmentsPagination = action.payload;
    },

    // Assignment creation
    setAssignmentCreateLoading: (state, action: PayloadAction<boolean>) => {
      state.assignmentCreateLoading = action.payload;
    },
    setAssignmentCreateFieldErrors: (
      state,
      action: PayloadAction<Record<string, string[]> | null>,
    ) => {
      state.assignmentCreateFieldErrors = action.payload;
    },

    // Selected items
    setSelectedAssignment: (
      state,
      action: PayloadAction<Assignment | null>,
    ) => {
      state.selectedAssignment = action.payload;
    },
    setSelectedRestaurant: (state, action: PayloadAction<string | null>) => {
      state.selectedRestaurant = action.payload;
    },
    setSelectedSupplier: (state, action: PayloadAction<string | null>) => {
      state.selectedSupplier = action.payload;
    },

    // Dialog states
    setAssignmentDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.assignmentDialogOpen = action.payload;
    },
    setViewAssignmentDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.viewAssignmentDialogOpen = action.payload;
    },

    // Clear state
    clearAssignmentState: (state) => {
      state.restaurantAssignments = null;
      state.supplierAssignments = null;
      state.selectedAssignment = null;
      state.selectedRestaurant = null;
      state.selectedSupplier = null;
      state.assignmentCreateFieldErrors = null;
    },
  },
});

// Export actions
export const {
  // Restaurant assignments
  setRestaurantAssignments,
  setRestaurantAssignmentsLoading,
  setRestaurantAssignmentsPagination,

  // Supplier assignments
  setSupplierAssignments,
  setSupplierAssignmentsLoading,
  setSupplierAssignmentsPagination,

  // Assignment creation
  setAssignmentCreateLoading,
  setAssignmentCreateFieldErrors,

  // Selected items
  setSelectedAssignment,
  setSelectedRestaurant,
  setSelectedSupplier,

  // Dialog states
  setAssignmentDialogOpen,
  setViewAssignmentDialogOpen,

  // Clear state
  clearAssignmentState,
} = assignmentSlice.actions;

// Export reducer
export default assignmentSlice.reducer;
